import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, FormControl, MenuItem, Select, Stack } from "@mui/material";

import Header from "../../Header";

import { api } from "../../../utils";
import { renderCellExpand } from "./renderCellExpand";
import { IError, ReportLoadingType } from "../../../models/ReportPageModels";
import { CustomNoRowsOverlay, ExportToolbar, PaginationModel } from "../../../models/ReportGridModels";

const billingsColumns = [
    { field: 'id', headerName: 'uniqueId', width: 1, sortable: false, hide: true, disableExport: true },
    { field: 'productCode', headerName: 'ProductCode', width: 200, sortable: true, editable: false, hide: false, },
    {
        field: 'description', headerName: 'Description', width: 400, sortable: true, editable: false,
        renderCell: renderCellExpand
    },
    { field: 'quantityStr', headerName: 'Quantity', width: 200, sortable: true, editable: false },
    { field: 'unitPriceStr', headerName: 'Unit Price', width: 100, sortable: true, editable: false },
    { field: 'vatStr', headerName: 'VAT', width: 100, sortable: true, editable: false },
    { field: 'priceStr', headerName: 'Price', width: 200, sortable: true, editable: false },
]

const columnVisibility = {
    id: false
}

interface BillingClientsResponseModel {
    data: BillingClientsData;
    error: IError;
}

interface BillingClientsData {
    clients: string[];
}


interface BillingReportResponseModel {
    data: BillingReportData;
    error: IError;
}

interface BillingReportData {
    report: BillingReportModel[];
}

interface BillingReportModel {
    id: string;
    productCode: string;
    description: string;
    quantityStr: string;
    unitPriceStr: string;
    vatStr: string;
    priceStr: string;
}

export interface BillingRequestModel {
    month: Dayjs;
    client: string;
}

function Billing() {
    const { enqueueSnackbar } = useSnackbar();

    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 50,
        page: 0,
    });

    const [month, setMonth] = useState<Dayjs>(dayjs());
    var monthCopy = useRef<Dayjs>(dayjs());

    const [clients, setClients] = useState<string[]>([]);
    const [selectedClient, setSelectedClient] = useState<string>("");

    const [billings, setBillings] = useState<BillingReportModel[]>([]);

    const [billingLoadingType, setBillingLoadingType] = useState<ReportLoadingType>(ReportLoadingType.SelectClient); // loading, error, noreports, selectClient

    useEffect(() => {
        GetClients();
    }, [])

    const GetClients = async () => {
        try {
            var resp: BillingClientsResponseModel = await api.get("api/Billing/GetClients", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setClients(resp.data.clients);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SelectClient(event: any) {
        var value = event.target.value;
        setSelectedClient(value);
    }

    const LoadBilling = async () => {
        setBillingLoadingType(ReportLoadingType.Loading)
        setBillings([]);

        var reqBody: BillingRequestModel = {
            month: monthCopy.current,
            client: selectedClient
        };

        try {
            var resp: BillingReportResponseModel = await api.post("api/Billing/Billing", {
                json: reqBody,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setBillings(resp.data.report);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
                setBillingLoadingType(ReportLoadingType.Error)
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
            setBillingLoadingType(ReportLoadingType.Error)
        }
    };


    function GetDatagridHeight() {
        return window.innerHeight - 100;
    }


    return (
        <>
            <Header headerType={"BILLING"} displayLogout={false} />
            <Stack direction='row'>
                <Stack
                    direction="column"
                    sx={{ width: '320px' }}
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={1.5}>
                    <p>Select month</p>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            format="MM/YYYY"
                            views={['year', 'month']}
                            openTo={'month'}
                            value={month}
                            onChange={(param: any) => {
                                setMonth(param);
                                monthCopy.current = dayjs(param);
                            }}
                        />
                    </LocalizationProvider>

                    <p>Client</p>
                    <FormControl sx={{ m: 1, width: 240 }}>
                        <Select
                            value={selectedClient}
                            onChange={SelectClient}
                            size="small"
                            defaultValue=''>
                            {clients.map((el, index) => (
                                <MenuItem value={el} key={index}>{el}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        onClick={LoadBilling}
                        sx={{ left: '49px' }}>
                        Create billing
                    </Button>
                </Stack>
                <div style={{ width: '100%', height: GetDatagridHeight() }}>
                    <DataGrid
                        rows={billings ?? []}
                        columns={billingsColumns}
                        columnVisibilityModel={columnVisibility}
                        pagination
                        pageSizeOptions={[10, 15, 20, 50, 100]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        keepNonExistentRowsSelected={true}
                        disableColumnMenu={false}
                        slots={{
                            toolbar: ExportToolbar,
                            noRowsOverlay: CustomNoRowsOverlay
                        }}
                        slotProps={{
                            noRowsOverlay: {
                                reportLoadingType: billingLoadingType
                            }
                        }}
                    />
                </div>
            </Stack>
        </>
    )
};

export default Billing;